/**
 * Order Desk-aligned packing slip HTML for London Smoke & Cure.
 * Shared by Printing Toolkit and Packers Toolkit so both print the same layout.
 */
import logoSrc from '../images/logo.png';
import unifiedProductService from '../services/unifiedProductService';
import { toOrderDeskCheckoutDate } from '../services/dateService';
import { getOrderDeliveryDate, getOrderDispatchDate } from './orderDateUtils';
import { renderOrderQrSvgMap } from './orderQrSvg';
import { formatOrderQrPayload } from './orderQrPayload';
import { buildPackingSlipLineModel } from './packingSlipModel';
import { paginatePackingSlipLines } from './packingSlipPagination';
import { enrichOrdersWithAccountDeliveryFields } from './enrichOrdersWithAccountDeliveryFields';
import { applyCompanyCheckoutLine } from './packingSlipCompany';
import { formatPackWeightKg } from '../services/packingLineWeightService';

export { applyCompanyCheckoutLine, resolvePackingSlipCompanyName } from './packingSlipCompany';

export const PACKING_SLIP_DEFAULT_LOGO_URL = logoSrc;
export const PACKING_SLIP_STORE_NAME = 'London Smoke & Cure';
export const PACKING_SLIP_STORE_TIMEZONE = 'Europe/London';

/** Canonical Standard A4 packing-slip inset. Never start a job below this. */
export const STANDARD_PACKING_SLIP_MARGIN_MM = 15;

export const STANDARD_PACKING_SLIP_MARGINS = Object.freeze({
  top: STANDARD_PACKING_SLIP_MARGIN_MM,
  right: STANDARD_PACKING_SLIP_MARGIN_MM,
  bottom: STANDARD_PACKING_SLIP_MARGIN_MM,
  left: STANDARD_PACKING_SLIP_MARGIN_MM,
});

const MARGIN_SIDES = ['top', 'right', 'bottom', 'left'];

/** Leftover defaults / corrupted saves — never treat these as an operator choice. */
const DISCARDED_MARGIN_SIDE_MM = new Set([0, 1, 5, 40, 55]);

const DISCARDED_MARGIN_PRESETS = [
  { top: 40, right: 20, bottom: 20, left: 20 },
  { top: 20, right: 20, bottom: 20, left: 20 },
];

export function createStandardPackingSlipMargins() {
  return { ...STANDARD_PACKING_SLIP_MARGINS };
}

const parseMarginSide = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return STANDARD_PACKING_SLIP_MARGIN_MM;
  const rounded = Math.round(parsed);
  if (DISCARDED_MARGIN_SIDE_MM.has(rounded)) return STANDARD_PACKING_SLIP_MARGIN_MM;
  if (rounded < 2 || rounded > 60) return STANDARD_PACKING_SLIP_MARGIN_MM;
  return rounded;
};

/**
 * Standard packing-slip page margins in millimetres.
 * Defaults and leftover 0/1/5/40/55 (and old 40/20/20/20 or 20/20/20/20) become 15 mm.
 * A value the operator typed in the current job (for example 12 or 18) is kept.
 */
export function normalizePackingSlipMargins(raw) {
  const source = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const matchesDiscardedPreset = DISCARDED_MARGIN_PRESETS.some((preset) =>
    MARGIN_SIDES.every((side) => Number(source[side]) === preset[side]),
  );
  if (matchesDiscardedPreset) return createStandardPackingSlipMargins();
  return {
    top: parseMarginSide(source.top),
    right: parseMarginSide(source.right),
    bottom: parseMarginSide(source.bottom),
    left: parseMarginSide(source.left),
  };
}

export function withNormalizedPackingSlipMargins(settings) {
  const next =
    settings && typeof settings === 'object' && !Array.isArray(settings)
      ? { ...settings }
      : {};
  next.margins = normalizePackingSlipMargins(next.margins);
  return next;
}

/** Defaults matching Order Desk packing slip custom CSS / @page. */
export const PACKING_SLIP_ORDERDESK_DEFAULTS = {
  paperSize: 'A4',
  orientation: 'portrait',
  margins: createStandardPackingSlipMargins(),
  fontSize: 9,
  lineHeight: 20,
  // Compact logo + QR so header fits with standard 15 mm @page margins
  logoWidth: 100,
  scalePercent: 100,
  includeDuplicate: true,
  showKgLines: true,
  showSignatureLines: true,
  footerText: `Thank you for placing an order with ${PACKING_SLIP_STORE_NAME}!`,
};

const sanitize = (str) => String(str ?? '').replace(/\uFFFD/g, '').trim();

const getItemSku = (item) => {
  const raw =
    item?.sku ??
    item?.code ??
    item?.Code ??
    item?.product_code ??
    item?.productCode ??
    item?.product_sku ??
    item?.productSku ??
    '';
  return String(raw || '').trim().toUpperCase();
};

/** Normalize pack size to a grams label (e.g. 50 → 50g). */
export function formatPackSizeAsGrams(packSize) {
  const raw = String(packSize ?? '').trim();
  if (!raw || raw === '—' || raw === '-') return '';
  if (/\d\s*(mg|g|kg)\b/i.test(raw)) {
    return raw.replace(/\s+/g, '');
  }
  if (/^\d+(\.\d+)?$/.test(raw)) return `${raw}g`;
  const m = raw.match(/^(\d+(?:\.\d+)?)\s*(.*)$/);
  if (m && !/kg|g|mg/i.test(m[2] || '')) {
    return `${m[1]}g`;
  }
  return raw;
}

/**
 * Packers Toolkit product name + pack size in grams (Order Desk style suffix).
 * Example: "Coppa | Retail Pack" + "50" → "Coppa | Retail Pack 50g"
 */
export function formatPackingSlipLineName(sku, fallbackName = '') {
  const productName = sku ? unifiedProductService.getProductName(sku) : '';
  const base =
    productName && productName !== 'Unknown Product'
      ? productName
      : sanitize(fallbackName || sku || 'Unknown Item');
  const grams = formatPackSizeAsGrams(sku ? unifiedProductService.getProductPackSize(sku) : '');
  if (!grams) return base;
  const compactGrams = grams.replace(/\s+/g, '').toLowerCase();
  const compactBase = base.replace(/\s+/g, '').toLowerCase();
  if (compactBase.includes(compactGrams)) return base;
  const numMatch = grams.match(/(\d+(?:\.\d+)?)\s*g\b/i);
  if (numMatch && new RegExp(`${numMatch[1]}\\s*g\\b`, 'i').test(base)) return base;
  return `${base} ${grams}`;
}

/** DD-MM-YYYY HH:mm in Europe/London (Order Desk store format). */
export function formatPackingSlipDateTime(value) {
  try {
    const d = value instanceof Date ? value : new Date(value || Date.now());
    if (Number.isNaN(d.getTime())) return '';
    const dtf = new Intl.DateTimeFormat('en-GB', {
      timeZone: PACKING_SLIP_STORE_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const parts = dtf.formatToParts(d);
    const g = (type) => parts.find((p) => p.type === type)?.value || '';
    return `${g('day').padStart(2, '0')}-${g('month').padStart(2, '0')}-${g('year')} ${g('hour').padStart(2, '0')}:${g('minute').padStart(2, '0')}`;
  } catch {
    return '';
  }
}

const formatDateOnlyDdMmYyyy = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(value.trim())) {
    return value.trim();
  }
  try {
    let d;
    if (value instanceof Date) {
      d = value;
    } else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      d = new Date(value);
    } else if (typeof value === 'string') {
      const m = value.trim().match(/^(\d{2})-(\d{2})-(\d{4})$/);
      if (m) return value.trim();
      d = new Date(value);
    } else {
      d = new Date(value);
    }
    if (Number.isNaN(d.getTime())) return sanitize(String(value));
    const dtf = new Intl.DateTimeFormat('en-GB', {
      timeZone: PACKING_SLIP_STORE_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const parts = dtf.formatToParts(d);
    const g = (type) => parts.find((p) => p.type === type)?.value || '';
    return `${g('day').padStart(2, '0')}-${g('month').padStart(2, '0')}-${g('year')}`;
  } catch {
    return sanitize(String(value));
  }
};

const needsKgLine = (name) => {
  const s = (name || '').toString().toLowerCase();
  return s.includes('whole') || s.includes('unsliced');
};

const getOrderItems = (order) => {
  if (Array.isArray(order?.originalItems) && order.originalItems.length) return order.originalItems;
  if (Array.isArray(order?.order_items) && order.order_items.length) return order.order_items;
  if (Array.isArray(order?.items) && order.items.length) return order.items;
  return [];
};

const getQuantityTotal = (order, items) => {
  const fromOrder = order?.quantity_total ?? order?.quantityTotal;
  if (fromOrder != null && fromOrder !== '' && !Number.isNaN(Number(fromOrder))) {
    return Number(fromOrder);
  }
  return items.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0);
};

const shouldPrintDuplicate = (order, items, qtyTotal) =>
  qtyTotal > 40 && items.length > 15;

const getShippingMethod = (order) =>
  sanitize(order?.shipping_method || order?.shippingMethod || '');

const getOrderNumber = (order) => {
  const raw = String(
    order?.source_id ||
      order?.sourceId ||
      order?.orderNumber ||
      order?.order_number ||
      order?.orderId ||
      order?.id ||
      '',
  ).trim();
  return raw.replace(/^#/, '');
};

const getCheckoutDataObject = (order) => {
  const cd = order?.checkout_data || order?.checkoutData;
  if (cd && typeof cd === 'object' && !Array.isArray(cd)) return cd;
  return null;
};

/**
 * Checkout / delivery lines after Shipping (Order Desk dumps checkout_data).
 * Dispatch / Delivery: OrderDesk checkout_data is source of truth (same as Admin Dashboard).
 * Falls back to Packers-normalized delivery/dispatch/notes when checkout_data is empty.
 * "Customer ID" is shown as Company (account or shipping company name).
 */
const buildCheckoutDataLines = (order) => {
  const lines = [];
  const cd = getCheckoutDataObject(order);
  if (cd) {
    Object.entries(cd).forEach(([key, val]) => {
      if (val == null || val === '') {
        lines.push({ key, val: '' });
        return;
      }
      lines.push({ key, val: String(val) });
    });
  }

  const findLineIndex = (name) =>
    lines.findIndex((l) => String(l.key).toLowerCase() === String(name).toLowerCase());

  const upsertDateLine = (key, raw) => {
    const formatted = toOrderDeskCheckoutDate(raw) || formatDateOnlyDdMmYyyy(raw);
    if (!formatted) return;
    const idx = findLineIndex(key);
    if (idx >= 0) {
      if (!String(lines[idx].val || '').trim()) {
        lines[idx] = { key: lines[idx].key, val: formatted };
      }
      return;
    }
    lines.push({ key, val: formatted });
  };

  // Prefer OrderDesk checkout_data (Admin / Packers OD path); helpers already check checkout_data first.
  const delivery = getOrderDeliveryDate(order) || '';
  const dispatch = getOrderDispatchDate(order) || '';
  const slipNote = sanitize(
    order?.packingSlipNote ||
      order?.packing_slip_note ||
      order?.customer?.packingSlipNote ||
      order?.customer?.packing_slip_note ||
      '',
  );
  const generalNotes = sanitize(order?.notes || order?.order_notes || order?.orderNotes || '');
  const notesParts = [];
  if (slipNote) notesParts.push(slipNote);
  if (generalNotes && generalNotes !== slipNote) notesParts.push(generalNotes);
  const notesCombined = notesParts.join(' - ');

  if (!cd || lines.length === 0) {
    if (delivery) lines.push({ key: 'Delivery', val: toOrderDeskCheckoutDate(delivery) || formatDateOnlyDdMmYyyy(delivery) });
    lines.push({ key: 'Notes', val: notesCombined });
    if (dispatch) lines.push({ key: 'Dispatch', val: toOrderDeskCheckoutDate(dispatch) || formatDateOnlyDdMmYyyy(dispatch) });
    return applyCompanyCheckoutLine(lines, order);
  }

  if (findLineIndex('Notes') < 0 && notesCombined) {
    lines.push({ key: 'Notes', val: notesCombined });
  }
  upsertDateLine('Delivery', delivery);
  upsertDateLine('Dispatch', dispatch);
  return applyCompanyCheckoutLine(lines, order);
};

const buildAddressLine = (parts) => parts.map(sanitize).filter(Boolean).join(', ');

const resolvePageCss = (settings) => {
  const s = { ...PACKING_SLIP_ORDERDESK_DEFAULTS, ...(settings || {}) };
  const margins = normalizePackingSlipMargins(s.margins);
  const topMm = margins.top;
  const rightMm = margins.right;
  const bottomMm = margins.bottom;
  const leftMm = margins.left;
  const mmToPx = (mm) => Math.round((mm * 96) / 25.4);
  const marginCss = `${topMm}mm ${rightMm}mm ${bottomMm}mm ${leftMm}mm`;
  // Pixel padding is what html2canvas actually respects for PDF capture
  const marginPaddingPxCss = `${mmToPx(topMm)}px ${mmToPx(rightMm)}px ${mmToPx(bottomMm)}px ${mmToPx(leftMm)}px`;
  const paperSize = s.paperSize || 'A4';
  const orientation = s.orientation || 'portrait';
  const pageSizeCss =
    s.pageSizeCss ||
    (paperSize === 'custom' || paperSize === 'label'
      ? 'A4 portrait'
      : `${paperSize} ${orientation}`);
  return {
    marginCss,
    marginPaddingPxCss,
    pageSizeCss,
    fontSize: Number(s.fontSize) || PACKING_SLIP_ORDERDESK_DEFAULTS.fontSize,
    lineHeight: Number(s.lineHeight) || PACKING_SLIP_ORDERDESK_DEFAULTS.lineHeight,
    logoWidth: Number(s.logoWidth || s.logoSize) || PACKING_SLIP_ORDERDESK_DEFAULTS.logoWidth,
    scale: (Number(s.scalePercent) || 100) / 100,
    includeDuplicate: s.includeDuplicate !== false,
    showKgLines: s.showKgLines !== false,
    showSignatureLines: s.showSignatureLines !== false,
    showPrices: s.showPrices === true,
    footerText: s.footerText || PACKING_SLIP_ORDERDESK_DEFAULTS.footerText,
  };
};

const buildCss = (resolved) => `
@page {
  size: ${resolved.pageSizeCss};
  /* Margins applied as .wrapper-table padding (px) — @page is ignored by html2canvas/PDF */
  margin: 0 !important;
  mso-header-margin: 0mm;
  mso-footer-margin: 0mm;
  mso-paper-source: 0;
}
html, body {
  margin: 0;
  padding: 0;
}
body {
  font-family: Arial, Helvetica, sans-serif;
  font-size: ${resolved.fontSize}pt;
  line-height: ${resolved.lineHeight}px;
  color: #000;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
  box-sizing: border-box;
}
.print-root {
  transform: scale(${resolved.scale});
  transform-origin: top left;
  width: 100%;
  box-sizing: border-box;
}
.noprint { display: none !important; }
.packing-slip-page {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  break-after: page;
  page-break-after: always;
  overflow: visible;
}
.packing-slip-page:last-child {
  break-after: auto;
  page-break-after: auto;
}
.main {
  height: fit-content;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  overflow: visible;
}
.main .wrapper {
  border: 0;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  overflow: visible;
}
.main .wrapper-table {
  /* px inset — reliable in browser print AND html2canvas PDF capture */
  padding: ${resolved.marginPaddingPxCss} !important;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  overflow: visible;
}
.slip-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin: 0 0 8px 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}
.slip-header .slip-meta {
  flex: 1 1 auto;
  min-width: 0;
}
.header_logo {
  float: none;
  flex: 0 0 ${resolved.logoWidth}px;
  flex-shrink: 0;
  width: ${resolved.logoWidth}px;
  max-width: ${resolved.logoWidth}px;
  margin: 0;
  padding: 0;
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-sizing: border-box;
  overflow: visible;
}
.header_logo img {
  width: 100%;
  height: auto;
  max-width: ${resolved.logoWidth}px;
  display: block;
  margin: 0;
  vertical-align: top;
}
.header_logo .order-qr {
  margin-top: 4px;
  width: 100%;
  max-width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
}
.header_logo .order-qr svg {
  width: 52px !important;
  max-width: 52px !important;
  height: 52px !important;
  aspect-ratio: 1 / 1;
  display: block;
  margin: 0 auto;
  box-sizing: border-box;
  overflow: visible;
}
.clearfix { clear: both; height: 0; }
ul.ul-plain { list-style: none; padding: 0; margin: 0; }
ul.ul-plain li { margin: 0; padding: 0; }
/* Company matches Printing Toolkit summary prominence — packers/riders scan this first */
ul.ul-plain li.slip-company-line {
  margin: 2px 0 4px;
  font-size: 12pt;
  line-height: 1.25;
  font-weight: 700;
}
ul.ul-plain li.slip-company-line b { font-weight: 800; }
.shippingmethod { font-size: 12pt; line-height: 22px; }
.ellipsis {
  max-width: 700px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}
.address_table { width: 100%; border-collapse: collapse; margin-top: 8px; }
.address_table td { width: 50%; padding: 0 8px 0 0; vertical-align: top; }
.product_table {
  margin-top: 10px;
  margin-bottom: 20px;
  width: 100%;
  max-width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}
.product_table th,
.product_table td { padding: 2px 4px; vertical-align: top; }
.product_table tr {
  break-inside: avoid;
  page-break-inside: avoid;
}
.product_table td {
  overflow-wrap: anywhere;
  word-break: normal;
}
.slip-food-note {
  display: block;
  margin-top: 1px;
  font-size: 8pt;
  color: #8c6828;
}
.qty_header,
.short_cell { text-align: center; width: 60px; }
.product_header { text-align: left; }
.product_table tr:nth-child(even) { background-color: #ededed; }
.product_table tr:nth-child(odd) { background-color: #ffffff; }
.kg_right { float: right; font-weight: bold; }
.sig-grid { width: 100%; border-collapse: collapse; margin-top: 8px; }
.sig-grid td { width: 50%; padding: 0; vertical-align: top; }
.page-break { page-break-before: always; }
.receipt_footer { text-align: center; margin-top: 8px; margin-bottom: 0; }
.shadowimg {
  width: 100%;
  height: 8px;
  margin: 4px 0 6px;
  background: linear-gradient(to bottom, rgba(0,0,0,0.18), transparent);
}
.slip-hr {
  width: 100%;
  border: 0;
  border-top: 1px solid #333;
  margin: 8px 0;
}
.packing-slip-page--compact {
  font-size: 8.5pt;
  line-height: 17px;
}
.packing-slip-page--compact .product_table th,
.packing-slip-page--compact .product_table td {
  padding-top: 1px;
  padding-bottom: 1px;
}
.packing-slip-page--dense {
  font-size: 8pt;
  line-height: 15px;
}
.packing-slip-page--dense .product_table {
  margin-top: 7px;
  margin-bottom: 12px;
}
.packing-slip-page--dense .product_table th,
.packing-slip-page--dense .product_table td {
  padding: 1px 3px;
}
.packing-slip-continuation {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: baseline;
  padding-bottom: 7px;
  border-bottom: 1px solid #333;
  font-weight: 700;
}
.packing-slip-continuation__page {
  white-space: nowrap;
}
`;

const getItemUnitPrice = (it) => {
  const raw =
    it?.price ??
    it?.unit_price ??
    it?.unitPrice ??
    it?.price_per_unit ??
    it?.Price ??
    null;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const formatGbp = (n) => {
  if (n == null || !Number.isFinite(n)) return '';
  return `£${n.toFixed(2)}`;
};

const buildProductRowsHtml = (items, order, weightsData, showKgLines, showPrices = false) => {
  const orderKey = order?.orderId || order?.source_id || order?.sourceId || order?.id;
  const orderWeights = (weightsData && orderKey && weightsData[orderKey]) || {};

  return items
    .map((it) => {
      const qty = Number(it.quantity) || 0;
      const sku = getItemSku(it);
      const name = sanitize(formatPackingSlipLineName(sku, it.name || ''));
      const unit = showPrices ? getItemUnitPrice(it) : null;
      const lineTotal = unit != null ? unit * qty : null;
      const variations =
        Array.isArray(it.variation_list)
          ? it.variation_list
          : it.variation_list && typeof it.variation_list === 'object'
            ? Object.entries(it.variation_list)
            : [];
      const variationsHtml =
        Array.isArray(variations) && variations.length
          ? `<ul class="ul-plain" style="margin-left:14px;">
               ${variations
                 .map((kv) => {
                   if (Array.isArray(kv)) {
                     const [k, v] = kv;
                     return `<li><b>${sanitize(k)}:</b> ${sanitize(String(v))}</li>`;
                   }
                   return `<li>${sanitize(String(kv))}</li>`;
                 })
                 .join('')}
             </ul>`
          : '';

      let kgSpan = '';
      if (showKgLines && needsKgLine(name)) {
        const recorded = orderWeights[sku];
        if (recorded != null && parseFloat(recorded) > 0) {
          kgSpan = `<span class="kg_right">${formatPackWeightKg(recorded)}kg</span>`;
        } else {
          kgSpan = `<span class="kg_right">___________kg</span>`;
        }
      }

      const priceCells = showPrices
        ? `<td align="right">${unit != null ? formatGbp(unit) : ''}</td>
              <td align="right">${lineTotal != null ? formatGbp(lineTotal) : ''}</td>`
        : '';

      return `
            <tr>
              <td align="center">${qty}</td>
              <td>
                ${name}
                ${kgSpan}
                ${variationsHtml}
              </td>
              ${priceCells}
            </tr>`;
    })
    .join('');
};

const buildProductModelRowsHtml = (lines, showPrices = false) =>
  (lines || [])
    .map((line) => {
      const variationsHtml = line.variations?.length
        ? `<ul class="ul-plain" style="margin-left:14px;">
             ${line.variations
               .map((variation) =>
                 variation.key
                   ? `<li><b>${sanitize(variation.key)}:</b> ${sanitize(variation.value)}</li>`
                   : `<li>${sanitize(variation.value)}</li>`,
               )
               .join('')}
           </ul>`
        : '';
      const kgSpan = line.needsKg
        ? `<span class="kg_right">${
            line.weightKg != null ? `${formatPackWeightKg(line.weightKg)}kg` : '___________kg'
          }</span>`
        : '';
      const priceCells = showPrices
        ? `<td align="right">${line.unitPrice != null ? formatGbp(line.unitPrice) : ''}</td>
           <td align="right">${line.lineTotal != null ? formatGbp(line.lineTotal) : ''}</td>`
        : '';
      return `
        <tr data-line-index="${line.index}">
          <td align="center">${line.quantity}</td>
          <td>
            ${sanitize(line.name)}
            ${kgSpan}
            ${line.foodNote ? `<small class="slip-food-note">${sanitize(line.foodNote)}</small>` : ''}
            ${variationsHtml}
          </td>
          ${priceCells}
        </tr>`;
    })
    .join('');

const buildSignatureHtml = (shipMethod, showSignatureLines) => {
  if (!showSignatureLines) return '';
  if (shipMethod === 'Bermondsey' || shipMethod === 'Cargo Bike') {
    return `
            <table class="address_table sig-grid" cellpadding="0" cellspacing="0" border="0">
              <tbody>
                <tr>
                  <td><b>Packed By:</b><br/><p></p></td>
                  <td><b>Received & Checked By:</b><br/><p></p></td>
                </tr>
              </tbody>
            </table>`;
  }
  return `
            <table class="address_table sig-grid" cellpadding="0" cellspacing="0" border="0">
              <tbody>
                <tr>
                  <td><b>Packed By:</b><br/><p></p></td>
                </tr>
              </tbody>
            </table>`;
};

/**
 * Build full multi-order packing slip HTML (Order Desk layout).
 * Async so each slip can embed an LSC:ORDER QR under the logo.
 * @param {Array} orders
 * @param {Object} [options]
 * @param {string} [options.logoUrl]
 * @param {Object} [options.weightsData] - { [orderId]: { [sku]: weightKg } }
 * @param {Object} [options.settings] - optional Printing Toolkit overrides (margins, scale, flags)
 * @param {boolean} [options.includeOrderQr=true]
 * @param {boolean} [options.enrichAccounts=true] - load Firestore account companyName for Company line
 */
export async function buildOrderDeskPackingSlipHTML(orders, options = {}) {
  let list = Array.isArray(orders) ? orders : [orders];
  if (options.enrichAccounts !== false) {
    const enriched = await enrichOrdersWithAccountDeliveryFields(list);
    if (Array.isArray(enriched)) list = enriched;
  }
  const logoUrl = options.logoUrl || PACKING_SLIP_DEFAULT_LOGO_URL;
  const weightsData = options.weightsData || {};
  const resolved = resolvePageCss(options.settings);
  const includeOrderQr = options.includeOrderQr !== false;

  // Compact QR under logo — kept inside logo column so PDF/print never clips the right edge
  const qrRenderSize = 52;
  const qrMap = includeOrderQr
    ? await renderOrderQrSvgMap(list, (order) => getOrderNumber(order), { size: qrRenderSize })
    : new Map();

  const blocks = list.flatMap((order) => {
    const items = getOrderItems(order);
    const qtyTotal = getQuantityTotal(order, items);
    const duplicate = shouldPrintDuplicate(order, items, qtyTotal);
    const shipMethod = getShippingMethod(order);
    const printedDate = formatPackingSlipDateTime(
      order?.date_added || order?.dateAdded || order?.date_updated || order?.dateUpdated || Date.now(),
    );
    const orderNumber = getOrderNumber(order);
    const qrSvg = orderNumber ? qrMap.get(orderNumber) || '' : '';
    const qrPayload = orderNumber ? formatOrderQrPayload(orderNumber) : '';
    const email = sanitize(order?.email || order?.customerEmail || order?.customer?.email || '');
    const phone = sanitize(
      order?.shipping?.phone || order?.customerPhone || order?.customer?.phone || '',
    );
    const checkoutLines = buildCheckoutDataLines(order);

    const invoiceName = `${sanitize(order?.customer?.first_name || order?.customerFirstName || '')} ${sanitize(order?.customer?.last_name || order?.customerLastName || '')}`.trim();
    const invoiceCompany = sanitize(
      order?.customer?.company || order?.customerCompany || order?.companyName || '',
    );
    const invoiceLine = buildAddressLine([
      order?.customer?.address1,
      order?.customer?.address2,
      order?.customer?.city,
      order?.customer?.postal_code || order?.customer?.postalCode,
    ]);

    const shipName = `${sanitize(order?.shipping?.first_name || order?.shippingFirstName || '')} ${sanitize(order?.shipping?.last_name || order?.shippingLastName || '')}`.trim();
    const shipCompany = sanitize(order?.shipping?.company || '');
    const shipLine = buildAddressLine([
      order?.shipping?.address1 || order?.shippingStreet || order?.shippingAddress?.line1,
      order?.shipping?.address2 || order?.shippingAddress?.line2,
      order?.shipping?.city || order?.shippingCity || order?.shippingAddress?.city,
      order?.shipping?.postal_code ||
        order?.shippingPostalCode ||
        order?.shipping?.postCode ||
        order?.shippingAddress?.postCode,
    ]);

    const lineModel = buildPackingSlipLineModel(order, {
      weightsData,
      showKgLines: resolved.showKgLines,
      showPrices: resolved.showPrices,
      formatLineName: formatPackingSlipLineName,
    });
    const pagination = paginatePackingSlipLines(lineModel.lines, {
      format: 'standard',
      showPrices: resolved.showPrices,
    });
    const priceHeader = resolved.showPrices
      ? `<th class="qty_header short_cell">Unit</th><th class="qty_header short_cell">Line</th>`
      : '';
    const totalRow = resolved.showPrices
      ? `<tr>
            <td align="center"><b>${lineModel.quantityTotal}</b></td>
            <td><b>Total</b></td>
            <td></td>
            <td align="right"><b>${lineModel.moneyTotal != null ? formatGbp(lineModel.moneyTotal) : sanitize(String(order?.total || order?.order_total || ''))}</b></td>
          </tr>`
      : `<tr>
            <td align="center"><b>${lineModel.quantityTotal}</b></td>
          </tr>`;
    const sigSection = buildSignatureHtml(shipMethod, resolved.showSignatureLines);
    const qrBlock = qrSvg
      ? `<div class="order-qr" title="${sanitize(qrPayload)}">${qrSvg}</div>`
      : '';

    const regularPages = pagination.chunks.map((chunk) => {
      const densityClass =
        pagination.profile.key === 'normal'
          ? ''
          : ` packing-slip-page--${pagination.profile.key}`;
      const firstPageHeader = chunk.isFirst
        ? `
          <div class="slip-header">
            <div class="slip-meta">
              <ul class="ul-plain">
                <li><b>Date:</b> ${sanitize(printedDate)}</li>
                <li><b>Order Number:</b> ${sanitize(orderNumber)}</li>
                <li><b>Email:</b> ${email}</li>
                ${phone ? `<li><b>Phone:</b> ${phone}</li>` : ''}
                <div class="shippingmethod">${shipMethod ? `<li><b>Shipping: ${sanitize(shipMethod)}</b></li>` : ''}</div>
                <div class="ellipsis">
                  ${checkoutLines
                    .map((row) => {
                      const isCompany = String(row.key || '').trim().toLowerCase() === 'company';
                      return `<li class="${isCompany ? 'slip-company-line' : ''}"><b>${sanitize(row.key)}:</b> ${sanitize(row.val)}</li>`;
                    })
                    .join('')}
                </div>
              </ul>
            </div>
            <div class="header_logo">
              <img src="${logoUrl}" width="${resolved.logoWidth}" alt="${PACKING_SLIP_STORE_NAME}">
              ${qrBlock}
            </div>
          </div>
          <table class="address_table" cellpadding="0" cellspacing="0" border="0">
            <tbody>
              <tr>
                <td>
                  <b>Invoice Address:</b><br/>
                  ${invoiceName || '&nbsp;'}<br/>
                  ${invoiceCompany ? `${invoiceCompany}<br/>` : ''}
                  ${invoiceLine}<br/>
                </td>
                <td>
                  <b>Shipping Address:</b><br/>
                  ${shipName || '&nbsp;'}<br/>
                  ${shipCompany ? `${shipCompany}<br/>` : ''}
                  ${shipLine}<br/>
                </td>
              </tr>
            </tbody>
          </table>`
        : `
          <div class="packing-slip-continuation">
            <span>Order Number: ${sanitize(orderNumber)}</span>
            <span class="packing-slip-continuation__page">Continuation ${chunk.pageNumber}/${chunk.pageCount}</span>
          </div>`;

      return `
<div class="packing-slip-page${densityClass}" data-print-order="${sanitize(orderNumber)}" data-page-index="${chunk.pageIndex}" data-page-count="${chunk.pageCount}" data-page-kind="${chunk.isFirst ? 'primary' : 'continuation'}">
  <div class="main">
    <div class="wrapper">
      <div class="wrapper-table">
        ${firstPageHeader}
        <table class="product_table" cellpadding="2" cellspacing="0" border="0">
          <thead>
            <tr>
              <th class="qty_header short_cell">Qty</th>
              <th class="product_header">Product</th>
              ${priceHeader}
            </tr>
          </thead>
          <tbody>
            ${buildProductModelRowsHtml(chunk.lines, resolved.showPrices)}
            ${chunk.isFinal ? totalRow : ''}
          </tbody>
        </table>
        ${chunk.isFinal ? `<p></p><p></p>${sigSection}` : ''}
      </div>
      ${chunk.isFinal ? '<hr class="slip-hr"><hr class="slip-hr">' : ''}
    </div>
    ${
      chunk.isFinal
        ? `<div class="shadowimg" aria-hidden="true"></div>
           <p class="receipt_footer">${sanitize(resolved.footerText)}</p>`
        : ''
    }
    <div class="clearfix"></div>
  </div>
</div>`;
    });

    if (!(resolved.includeDuplicate && duplicate)) return regularPages;

    const duplicatePagination = paginatePackingSlipLines(lineModel.lines, {
      format: 'standard',
      showPrices: false,
      profile: {
        ...pagination.profile,
        firstCapacity: pagination.profile.continuationCapacity,
      },
    });
    const duplicatePages = duplicatePagination.chunks.map((chunk) => `
<div class="packing-slip-page${
      duplicatePagination.profile.key === 'normal'
        ? ''
        : ` packing-slip-page--${duplicatePagination.profile.key}`
    }" data-print-order="${sanitize(orderNumber)}" data-page-kind="duplicate" data-page-index="${chunk.pageIndex}" data-page-count="${chunk.pageCount}">
  <div class="main">
    <div class="wrapper">
      <div class="wrapper-table">
        <div class="packing-slip-continuation">
          <span>Duplicate · Order Number: ${sanitize(orderNumber)}</span>
          <span class="packing-slip-continuation__page">${chunk.pageNumber}/${chunk.pageCount}</span>
        </div>
        <table class="product_table" cellpadding="2" cellspacing="0" border="0">
          <thead><tr><th class="qty_header short_cell">Qty</th><th class="product_header">Product</th></tr></thead>
          <tbody>
            ${buildProductModelRowsHtml(chunk.lines, false)}
            ${chunk.isFinal ? `<tr><td align="center"><b>${lineModel.quantityTotal}</b></td><td><b>Total</b></td></tr>` : ''}
          </tbody>
        </table>
      </div>
    </div>
  </div>
</div>`);
    return [...regularPages, ...duplicatePages];
  });

  return `
<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>Packing Slips</title>
<style>${buildCss(resolved)}</style>
</head>
<body>
<div class="print-root">
${blocks.join('\n')}
</div>
</body>
</html>
  `.trim();
}

/**
 * Packers Toolkit / silent-print compatible signature:
 * buildPackingSlipHTML(orders, logoUrl?, weightsData?) -> Promise<string>
 */
export async function buildPackingSlipHTML(orders, logoUrl, weightsData = {}) {
  return buildOrderDeskPackingSlipHTML(orders, {
    logoUrl: logoUrl || PACKING_SLIP_DEFAULT_LOGO_URL,
    weightsData: weightsData && typeof weightsData === 'object' ? weightsData : {},
    settings: PACKING_SLIP_ORDERDESK_DEFAULTS,
  });
}

export default buildOrderDeskPackingSlipHTML;
